import {AsyncPipe} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConsultationPopupRibbonService} from '@services/core/consultation-popup/consultation-popup-ribbon.service';
import {AppAuthService} from '@services/core/auth/app-auth.service';
import {UserCapabilitiesService} from '@services/core/capabilities/user-capabilities.service';
import {IRouteStructure} from '@shared/common/routing/IRouteStructure';
import {ClinicianDashboardRoutes} from '@shared/common/routing/clinician-dashboard-routes';
import {SidebarComponent} from '@shared/modules/layout-components/sidebar/sidebar.component';
import {HeaderComponent} from '@shared/modules/layout-components/header/header.component';
import {CFTranslatorUtil} from '@shared/utils/code-feature-translation/CFTranslator.util';
import {IconMap} from '@shared/common/icons';
import {
  AppointmentRibbonComponent,
} from '@app/areas/appointments/components/appointment-ribbon/appointment-ribbon.component';
import {ChatSidebarBadgeService} from '@app/areas/chat/services/chat-sidebar-badge.service';
import {PatientsSidebarBadgeService} from '@app/areas/care-team/services/patients-sidebar-badge.service';
import {ConsultsSidebarBadgeService} from '@app/areas/appointments/services/consults-sidebar-badge.service';
import {SupportCasesBacklogBadgeService} from '@app/areas/crm/support-cases/services/support-cases-backlog-badge.service';
import {WorkspaceSwitcherService} from '@services/core/workspace-switcher/workspace-switcher.service';
import {ModalService} from '@services/helpers/modal/modal.service';
import {FeedbackModalComponent} from '@app/areas/crm/support-cases/components/feedback-modal/feedback-modal.component';
import {
  ADMIN_NAVIGATION_ITEMS,
  AdminNavigationItem,
  getAccessibleAdminNavigationItems,
  getFirstAccessibleAdminNavigationItem,
  isAdminNavigationUrl,
} from './admin-navigation';
import {
  getAccessibleSettingsNavigationItems,
  getFirstAccessibleSettingsNavigationItem,
  isSettingsNavigationUrl,
  SETTINGS_NAVIGATION_ITEMS,
} from './settings-navigation';

@Component({
  selector: 'app-clinician-dashboard-index',
  templateUrl: './clinician-dashboard-index.component.html',
  styleUrls: ['./clinician-dashboard-index.component.scss'],
  standalone: true,
  imports: [HeaderComponent, SidebarComponent, RouterOutlet, AsyncPipe, AppointmentRibbonComponent],
})
export class ClinicianDashboardIndexComponent implements OnInit, OnDestroy {
  private readonly unsub = new Subject<void>();
  private currentUrl: string;

  private readonly basePrimarySidebarItems: IRouteStructure[] = [
    {
      type: 'url',
      localPath: '',
      fullUrl: ClinicianDashboardRoutes.PATIENT_LIST.list?.fullUrl ?? '/cn/patient-list',
      localeKey: ClinicianDashboardRoutes.PATIENT_LIST.list?.localeKey ?? 'Patients',
      translationDefault: ClinicianDashboardRoutes.PATIENT_LIST.list?.translationDefault ?? 'Patients',
      icon: ClinicianDashboardRoutes.PATIENT_LIST.list?.icon ?? IconMap.accountGroupOutline,
    },
    {
      type: 'url',
      localPath: '',
      fullUrl: ClinicianDashboardRoutes.CONSULTS.list?.fullUrl ?? '/cn/consults',
      localeKey: ClinicianDashboardRoutes.CONSULTS.list?.localeKey ?? 'Consults',
      translationDefault: ClinicianDashboardRoutes.CONSULTS.list?.translationDefault ?? 'Consults',
      icon: ClinicianDashboardRoutes.CONSULTS.list?.icon ?? IconMap.calendar,
    },
    {
      type: 'url',
      localPath: '',
      fullUrl: `/${ClinicianDashboardRoutes.DASHBOARD_PREFIX}/${CFTranslatorUtil.Prescriptions.snakeCase}`,
      localeKey: CFTranslatorUtil.Builder.default,
      translationDefault: 'Prescription\nBuilder',
      icon: IconMap.medicalTx,
    },
  ];

  private readonly chatSidebarBadge = inject(ChatSidebarBadgeService);
  private readonly patientsSidebarBadge = inject(PatientsSidebarBadgeService);
  private readonly consultsSidebarBadge = inject(ConsultsSidebarBadgeService);
  private readonly supportCasesBacklogBadge = inject(SupportCasesBacklogBadgeService);
  private readonly capabilitiesService = inject(UserCapabilitiesService);
  private readonly modalService = inject(ModalService);

  get canAccessSupportCases(): boolean {
    return this.authService.hasRole('developer');
  }

  constructor(
    public consultationPopup: ConsultationPopupRibbonService,
    private readonly authService: AppAuthService,
    private readonly router: Router,
    public readonly workspaceSwitcher: WorkspaceSwitcherService,
  ) {
    this.currentUrl = this.router.url;

    this.router.events
      .pipe(takeUntil(this.unsub))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.currentUrl = event.urlAfterRedirects;
        }
      });
  }

  ngOnInit(): void {
    void this.chatSidebarBadge.refresh();
    void this.patientsSidebarBadge.refresh();
    void this.consultsSidebarBadge.refresh();

    if (this.canAccessSupportCases) {
      void this.supportCasesBacklogBadge.refresh();
    }

    this.authService?.currentUser
      ?.pipe?.(takeUntil(this.unsub))
      ?.subscribe?.(user => {
        if (user?.identityProfile?.roles?.includes('developer')) {
          void this.supportCasesBacklogBadge.refresh();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }

  get primarySidebarItems(): IRouteStructure[] {
    const items = [
      this.buildPatientsItem(),
      this.buildConsultsItem(),
      this.buildChatItem(),
    ];

    if (this.capabilitiesService.hasCapabilitySignal('prescription_builder:create')() ||
        this.capabilitiesService.hasCapabilitySignal('prescription_builder:view')()) {
      items.push(this.basePrimarySidebarItems[2]);
    }

    return items;
  }

  get adminSidebarItems(): AdminNavigationItem[] {
    const items = getAccessibleAdminNavigationItems(role => this.authService.hasRole(role));
    return items.map(item => {
      if (item.fullUrl === '/cn/developer/support-cases') {
        return {
          ...item,
          badgeCount: this.canAccessSupportCases ? this.supportCasesBacklogBadge.count() : undefined,
        };
      }
      return item;
    });
  }

  get adminEntryPoint(): IRouteStructure | null {
    const item = getFirstAccessibleAdminNavigationItem(role => this.authService.hasRole(role));

    return item
      ? {
          ...item,
          icon: 'mdi-security',
        }
      : null;
  }

  get settingsSidebarItems(): IRouteStructure[] {
    const items = [...getAccessibleSettingsNavigationItems()];
    if (this.workspaceSwitcher.isLegalEntityOwner$()) {
      items.push({
        ...ClinicianDashboardRoutes.ORGANISATION_SETTINGS.view,
        translationDefault: 'Organisation settings',
      });
    }
    return items;
  }

  get settingsEntryPoint(): IRouteStructure | null {
    return getFirstAccessibleSettingsNavigationItem();
  }

  get adminTotalBadgeCount(): number {
    let total = 0;
    for (const item of this.adminSidebarItems) {
      if (item.badgeCount && item.badgeCount > 0) {
        total += item.badgeCount;
      }
    }
    return total;
  }

  get footerActions(): IRouteStructure[] {
    const adminTotal = this.adminTotalBadgeCount;
    return [
      ...(this.adminEntryPoint
        ? [{
            ...this.adminEntryPoint,
            translationDefault: 'Admin',
            badgeCount: adminTotal > 0 ? adminTotal : undefined,
          }]
        : []),
      this.buildFeedbackItem(),
      ...(this.settingsEntryPoint
        ? [{
            ...this.settingsEntryPoint,
            translationDefault: 'Settings',
            icon: IconMap.settings,
          }]
        : []),
    ];
  }

  get showAdminRail(): boolean {
    return isAdminNavigationUrl(this.currentUrl);
  }

  get showSettingsRail(): boolean {
    return isSettingsNavigationUrl(this.currentUrl);
  }

  get primarySidebarInactivePrefixes(): string[] {
    return [
      ...ADMIN_NAVIGATION_ITEMS.map(item => item.fullUrl as string),
      ...SETTINGS_NAVIGATION_ITEMS.map(item => item.fullUrl as string),
    ];
  }

  private buildPatientsItem(): IRouteStructure {
    return {
      ...this.basePrimarySidebarItems[0],
      badgeCount: this.patientsSidebarBadge.count(),
    };
  }

  private buildConsultsItem(): IRouteStructure {
    return {
      ...this.basePrimarySidebarItems[1],
      badgeCount: this.consultsSidebarBadge.count(),
    };
  }

  private buildChatItem(): IRouteStructure {
    return {
      type: 'url',
      localPath: '',
      fullUrl: ClinicianDashboardRoutes.CHAT.list?.fullUrl ?? '/cn/chat',
      localeKey: ClinicianDashboardRoutes.CHAT.list?.localeKey ?? 'Chat',
      translationDefault: ClinicianDashboardRoutes.CHAT.list?.translationDefault ?? 'Chat',
      icon: ClinicianDashboardRoutes.CHAT.list?.icon ?? IconMap.chat,
      badgeCount: this.chatSidebarBadge.count(),
    };
  }

  private buildFeedbackItem(): IRouteStructure {
    return {
      type: 'action',
      action: () => this.modalService.open(FeedbackModalComponent),
      localeKey: 'Feedback',
      translationDefault: 'Feedback',
      icon: IconMap.messageDraw,
    };
  }
}
